import { FaCompressArrowsAlt, FaColumns, FaSwimmingPool, FaList, FaSortAmountDown, FaSortAmountUp, FaUsersCog } from 'react-icons/fa';

/**
 * SearchAndFilterBar Component
 * Renders the search input and action buttons (Filters, Export, View Toggle, Merge).
 * Provides filtering, exporting, and view switching capabilities for action items.
 * Admin users also get access to the Merge Items action directly in this bar.
 * 
 * @param {Object}   props
 * @param {string}   props.searchQuery              - Current search query
 * @param {Function} props.onSearchChange            - Function to handle search query changes
 * @param {boolean}  props.showFilters               - Whether filters panel is visible
 * @param {Function} props.onToggleFilters            - Function to toggle filters panel visibility
 * @param {Function} props.onExport                  - Function to handle export to Excel
 * @param {string}   props.viewMode                  - Current view mode ('kanban', 'swimlanes', 'list')
 * @param {Function} props.onViewModeChange          - Function to change the view mode
 * @param {boolean}  props.isAdmin                   - Whether the current user is an admin (shows merge button)
 * @param {number}   props.selectedItemsCount         - Number of items currently selected (for merge button label)
 * @param {Function} props.onOpenMergeModal           - Callback to open the merge modal
 * @param {string}   props.swimGroupBy               - Current swimlane group-by field ('function', 'property', 'assignee', 'type')
 * @param {Function} props.onSwimGroupByChange       - Callback when swimlane group-by changes
 * @param {string}   props.sortField                 - Current sort field key
 * @param {string}   props.sortDirection              - Current sort direction ('asc' | 'desc')
 * @param {Function} props.onSortChange               - Callback (field, direction) when sort changes
 */
const SearchAndFilterBar = ({
    searchQuery,
    onSearchChange,
    showFilters,
    onToggleFilters,
    onExport,
    viewMode = 'kanban',
    onViewModeChange,
    isAdmin = false,
    selectedItemsCount = 0,
    onOpenMergeModal,
    swimGroupBy = 'function',
    onSwimGroupByChange,
    sortField = 'lastUpdated',
    sortDirection = 'desc',
    onSortChange
}) => {
    const handleSortFieldChange = (e) => {
        onSortChange && onSortChange(e.target.value, sortDirection);
    };
    const handleSortDirectionToggle = () => {
        onSortChange && onSortChange(sortField, sortDirection === 'asc' ? 'desc' : 'asc');
    };
    return (
        <div className="search-filter-bar">
            <div className="search-container">
                <input
                    type="text"
                    placeholder="Search by title, description, property, or tenant..."
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="search-input"
                />
            </div>
            <div className="filter-buttons">
                <button
                    type="button"
                    className={`btn-filter ${showFilters ? 'active' : ''}`}
                    onClick={onToggleFilters}
                >
                    <span className="filter-icon">⚙</span>
                    {showFilters ? "Hide Filters" : "Filters"}
                </button>
                <button 
                    type="button" 
                    className="btn-export"
                    onClick={onExport}
                    title="Export to Excel"
                >
                    📊 Export
                </button>

                {/* Merge button — admin only, always visible in list view.
                    In Kanban/swimlane view it prompts the user to switch to list view first. */}
                {isAdmin && (
                    viewMode === 'list' ? (
                        <button
                            type="button"
                            className={`btn-merge-toolbar ${selectedItemsCount >= 2 ? 'ready' : ''}`}
                            onClick={onOpenMergeModal}
                            disabled={selectedItemsCount < 2}
                            title={
                                selectedItemsCount < 2
                                    ? 'Select at least 2 items in the list below to merge them'
                                    : `Merge ${selectedItemsCount} selected items`
                            }
                        >
                            <FaCompressArrowsAlt />
                            {selectedItemsCount >= 2
                                ? `Merge (${selectedItemsCount})`
                                : 'Merge Items'}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn-merge-toolbar"
                            onClick={() => onViewModeChange('list')}
                            title="Switch to List View to select and merge action items"
                        >
                            <FaCompressArrowsAlt />
                            Merge Items
                        </button>
                    )
                )}

                {/* View Mode Segmented Control */}
                <div className="view-mode-toggle" role="group" aria-label="View mode">
                    <button
                        type="button"
                        className={`view-mode-btn ${viewMode === 'swimlanes' ? 'active' : ''}`}
                        onClick={() => onViewModeChange('swimlanes')}
                        title="Swimlanes View"
                    >
                        <FaSwimmingPool />
                        <span>Swimlanes</span>
                    </button>
                    <button
                        type="button"
                        className={`view-mode-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                        onClick={() => onViewModeChange('kanban')}
                        title="Kanban View"
                    >
                        <FaColumns />
                        <span>Kanban</span>
                    </button>
                    <button
                        type="button"
                        className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => onViewModeChange('list')}
                        title="List View"
                    >
                        <FaList />
                        <span>List</span>
                    </button>
                </div>

                {/* Group By Dropdown — visible only in swimlanes view */}
                {viewMode === 'swimlanes' && onSwimGroupByChange && (
                    <div className="group-by-control">
                        <label htmlFor="swimGroupBy">Group by:</label>
                        <select
                            id="swimGroupBy"
                            value={swimGroupBy}
                            onChange={(e) => onSwimGroupByChange(e.target.value)}
                            className="group-by-select"
                        >
                            <option value="function">Function</option>
                            <option value="property">Property</option>
                            <option value="assignee">Assignee</option>
                            <option value="type">Type</option>
                        </select>
                    </div>
                )}

                {/* Sort Controls */}
                {onSortChange && (
                    <div className="sort-control">
                        <label htmlFor="sortField">Sort by:</label>
                        <select
                            id="sortField"
                            value={sortField}
                            onChange={handleSortFieldChange}
                            className="group-by-select"
                        >
                            <option value="lastUpdated">Last Updated</option>
                            <option value="lastResponse">Last Response</option>
                            <option value="createdDate">Created Date</option>
                            <option value="dueDate">Due Date</option>
                            <option value="priority">Priority</option>
                            <option value="title">Title</option>
                        </select>
                        <button
                            type="button"
                            className="btn-sort-direction"
                            onClick={handleSortDirectionToggle}
                            title={sortDirection === 'asc' ? 'Ascending — click to switch to descending' : 'Descending — click to switch to ascending'}
                            aria-label={`Sort ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
                        >
                            {sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchAndFilterBar;
